import { FiberManualRecord } from '@mui/icons-material';
import {
  CircularProgress,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  TooltipProps,
  Typography,
  styled,
  tooltipClasses,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { forwardRef, useEffect, useState } from 'react';
import { Link, LinkProps, useLocation } from 'react-router-dom';

import { useNav } from '../../../hooks/useNav';
import { NavItemType } from '../../../types/types';

interface NavItemProps {
  item: NavItemType;
  level: number;
}

export const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    fontSize: 14,
    boxShadow: theme.shadows[1],
  },
}));

const NavItem = ({ item, level }: NavItemProps) => {
  const theme = useTheme();
  const { pathname } = useLocation();
  const [loading, setLoading] = useState(false);
  const { drawerOpen, openItem, selectedID, setSelectedID } = useNav();

  const isSelected = selectedID === item.id;

  let itemTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  // Create a properly typed forwardRef component
  const LinkRef = forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => (
    <Link ref={ref} {...props} to={item.url || ''} state={item.state} />
  ));
  LinkRef.displayName = 'LinkRef';

  let listItemProps: {
    component: any;
    to?: string;
    href?: string;
    target?: string;
  } = { component: LinkRef };

  if (item.external) {
    listItemProps = { component: 'a', href: item.url, target: itemTarget };
  }

  const handleClick = async () => {
    if (item.id) {
      setSelectedID(item.id);
    }
    if (item.onClick) {
      setLoading(true);
      await item.onClick();
      setLoading(false);
    }
  };

  const Icon = item.icon;

  const itemIcon = item.icon ? (
    <Icon
      style={{
        fontSize: drawerOpen ? '1rem' : '1.25rem',
        color: isSelected
          ? theme.sidebar.navItem.selected.text
          : theme.sidebar.navItem.default.text,
      }}
    />
  ) : (
    <FiberManualRecord
      sx={{
        width: openItem.findIndex((id) => id === item.id) > -1 ? 8 : 6,
        height: openItem.findIndex((id) => id === item.id) > -1 ? 8 : 6,
        color: theme.sidebar.navItem.default.text,
      }}
    />
  );

  // active menu item on page load
  useEffect(() => {
    if (
      item.id &&
      item.url &&
      (pathname === item.url || pathname.includes(item.url))
    ) {
      setSelectedID(item.id);
    }
  }, [pathname, item.url, item.id, setSelectedID]);

  const shouldShow =
    typeof item.isVisible === 'function'
      ? item.isVisible({})
      : item.isVisible !== false;

  if (!shouldShow) {
    return null;
  }

  const mainContent = (
    <ListItemButton
      {...listItemProps}
      disabled={item.disabled || loading}
      onClick={handleClick}
      selected={isSelected}
      sx={{
        zIndex: 1201,
        pl: drawerOpen ? `${level === 1 ? 16 : 42}px` : 1.5,
        py: 0.5,
        color: isSelected
          ? theme.sidebar.navItem.selected.text
          : theme.sidebar.navItem.default.text,
        ...(drawerOpen && {
          '&:hover': {
            bgcolor: theme.sidebar.navItem.default.hover.background,
          },
          '&.Mui-selected': {
            bgcolor: theme.sidebar.navItem.selected.background,
            borderRight: theme.sidebar.navItem.selected.borderRight,
            color: theme.sidebar.navItem.selected.text,
            '&:hover': {
              color: theme.sidebar.navItem.selected.hover.text,
              bgcolor: theme.sidebar.navItem.selected.hover.background,
            },
          },
        }),
        ...(!drawerOpen && {
          '&:hover': {
            bgcolor: 'transparent',
          },
          '&.Mui-selected': {
            '&:hover': {
              bgcolor: 'transparent',
            },
            bgcolor: 'transparent',
          },
        }),
      }}
    >
      {loading ? (
        <ListItemIcon>
          <CircularProgress size={15} sx={{ mr: 1.7 }} />
        </ListItemIcon>
      ) : (
        <ListItemIcon
          sx={{
            minWidth: 26,
            ...(!drawerOpen && {
              borderRadius: 1.5,
              width: 36,
              height: 36,
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                bgcolor: theme.sidebar.navItem.default.hover.background,
              },
            }),
            ...(!drawerOpen &&
              isSelected && {
                bgcolor: theme.sidebar.navItem.selected.background,
                '&:hover': {
                  bgcolor: theme.sidebar.navItem.selected.hover.background,
                },
              }),
          }}
        >
          {itemIcon}
        </ListItemIcon>
      )}

      {(drawerOpen || (!drawerOpen && level !== 1)) && (
        <ListItemText
          primary={
            <Typography
              sx={{
                color: isSelected
                  ? theme.sidebar.navItem.selected.text
                  : theme.sidebar.navItem.default.text,
                fontSize: '0.875rem',
              }}
            >
              {item.title}
            </Typography>
          }
          secondary={
            item.caption && (
              <Typography
                variant="caption"
                sx={{
                  color: theme.sidebar.navItem.default.text,
                  opacity: 0.6,
                }}
                display="block"
                gutterBottom
              >
                {item.caption}
              </Typography>
            )
          }
        />
      )}
    </ListItemButton>
  );

  return (
    <>
      {!drawerOpen ? (
        <LightTooltip
          title={item.title}
          placement="right"
          arrow
          disableInteractive={true}
        >
          {mainContent}
        </LightTooltip>
      ) : (
        mainContent
      )}
    </>
  );
};

export default NavItem;
